<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <CButton color="link" class="px-0 mb-2" @click="backToList">
              <CIcon name="cil-arrow-left" /> Receipts
            </CButton>
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? (obj.companyName || "Receipt") : "Add receipt" }}
            </h1>
            <p class="text-muted mb-0">Upload a receipt photo, extract its details, then check and save</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              v-if="obj.id"
              color="light"
              class="mr-2"
              aria-label="Previous receipt"
              @click="previous"
            >
              <CIcon name="cil-chevron-left" />
            </CButton>
            <CButton
              v-if="obj.id"
              color="light"
              class="mr-2"
              aria-label="Next receipt"
              @click="next"
            >
              <CIcon name="cil-chevron-right" />
            </CButton>
            <CButton v-if="obj.id" color="light" @click="addNew">
              <CIcon name="cil-plus" /> Add receipt
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow>
      <CCol lg="8">
        <CCard>
          <CCardHeader class="bg-transparent">
            <h2 class="h6 font-weight-bold mb-0">Receipt image</h2>
          </CCardHeader>
          <CCardBody>
            <!-- Extraction progress -->
            <div v-if="isLoading" class="mb-4">
              <CProgress :value="progressValue" class="mb-1">
                <CProgressBar :value="progressValue" color="primary" />
              </CProgress>
              <small class="text-muted">Extracting details… {{ progressValue }}%</small>
            </div>

            <CRow>
              <CCol sm="6">
                <CImg
                  v-if="obj.documentId"
                  :src="billImageUrl"
                  class="mb-3"
                  responsive
                  :alt="`Receipt from ${obj.companyName || 'vendor'}`"
                />
              </CCol>
            </CRow>
            <WidgetsUploadImage :billImageUrl="billImageUrl" @uploaded="uploaded" />
          </CCardBody>
          <CCardFooter class="d-flex justify-content-end">
            <CButton color="primary" variant="outline" :disabled="isLoading || !obj.documentId" @click="extract">
              Extract details
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
      <CCol lg="4">
        <CCard>
          <CCardHeader class="bg-transparent">
            <h2 class="h6 font-weight-bold mb-0">Receipt details</h2>
          </CCardHeader>
          <CCardBody>
            <CInput
              label="Company name"
              v-model="obj.companyName"
              @input="onCompanyNameChange"
            />
            <div class="form-group">
              <label for="receipt-date">Date and time</label>
              <input
                id="receipt-date"
                type="datetime-local"
                class="form-control"
                :value="computeBillDate"
                @change="setDateFilter"
              />
            </div>
            <CInput
              label="Total amount"
              inputmode="decimal"
              v-model="obj.totalAmount"
            >
              <template #prepend-content>RM</template>
            </CInput>
          </CCardBody>
          <CCardFooter class="d-flex justify-content-between">
            <CButton color="light" :disabled="saving" @click="backToList">Cancel</CButton>
            <CButton color="primary" :disabled="saving || isLoading" @click="submit">
              <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
              {{ obj.id ? "Save" : "Add receipt" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>
<script>
import ReceiptApi from "../../lib/receiptApi";
import ResitAiApi from "@/lib/resitaiApi";
import DocumentApi from "@/lib/documentApi";

import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage.vue";
import { toApiInstant } from "@/utils/datetime";

// Value for <input type="datetime-local">: "YYYY-MM-DDTHH:mm" in the browser's local time.
function toLocalInputValue(value) {
  const dt = value instanceof Date ? value : new Date(value);
  if (!value || isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export default {
  name: "Receipt",
  components: {
    WidgetsUploadImage,
  },
  data: () => {
    return {
      selectedChartOfAccount: null,
      chartOfAccountItems: [],
      isLoading: false, // OCR extraction in progress
      isLoadingSearchChartOfAccount: false,
      progressValue: 0,
      documentApi: new DocumentApi(),
      uploadedFiles: [],
      obj: {},
      billDateTime: new Date(),
      submitted: false,
      saving: false,
      api: new ReceiptApi(),
      resitAiApi: new ResitAiApi(),
      chartOfAccountApi: new ChartOfAccountApi(),
      loading: false,
    };
  },
  mounted() {
    var self = this;

    self.refreshChartOfAccount();
    self.resetObj();

    this.debounceSearchChartOfAccount = this.debounce(this.searchChartOfAccount, 1000);
  },
  watch: {
    // Prev/next navigate between /tenants/Receipt/:id routes that reuse this component.
    "$route.params.id"() {
      this.resetObj();
    },
  },
  computed: {
    billImageUrl() {
      var self = this;
      return apiUrl + "documents/file/" + self.obj.documentId;
    },
    computeBillDate() {
      return toLocalInputValue(this.billDateTime);
    },
  },

  methods: {
    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          func.apply(this, args);
        }, delay);
      };
    },

    searchChartOfAccount(merchantName) {
      var suggestDto = {
        businessId: this.obj.businessId,
        merchantName: merchantName,
      };

      this.isLoadingSearchChartOfAccount = true;
      this.chartOfAccountApi
        .suggestChartOfAccount(suggestDto)
        .then((response) => {
          this.selectedChartOfAccount = response.result;
        })
        .catch((error) => {
          console.error("Error suggesting chart of account:", error);
        })
        .finally(() => {
          this.isLoadingSearchChartOfAccount = false;
        });
    },

    debounceSearchChartOfAccount: null,
    onCompanyNameChange() {
      this.debounceSearchChartOfAccount(this.obj.companyName);
    },

    refreshChartOfAccount() {
      var self = this;
      self.chartOfAccountApi
        .getListByCurrentBusiness()
        .then((response) => {
          self.chartOfAccountItems = response.result;
        })
        .catch(() => {});
    },
    extractImageFromUrl(imageUrl) {
      this.isLoading = true;
      this.progressValue = 0;

      const interval = setInterval(() => {
        if (this.progressValue < 95) {
          this.progressValue += 5;
        }
      }, 500);

      fetch(imageUrl)
        .then((response) => response.blob())
        .then((imageBlob) => {
          const formData = new FormData();
          formData.append("file", imageBlob, "image.jpg");
          formData.append("ocr_engine", "googlevision");
          const resitAiApi = new ResitAiApi();
          return resitAiApi.extract(formData);
        })
        .then((result) => {
          this.progressValue = 100;
          this.$toast.success("Details extracted. Check them before saving");

          this.obj.companyName = this.getMerchantName(result.parsed_data);
          this.obj.billNo = result.parsed_data.billNo;
          this.billDateTime = this.getDateTime(result.parsed_data);
          this.obj.totalAmount = this.getAmount(result.parsed_data);

          this.debounceSearchChartOfAccount(this.obj.companyName);
        })
        .catch((error) => {
          console.error("Error extracting data:", error);
          this.$toast.error("Couldn't read this receipt. Enter the details yourself, or try a clearer photo.");
        })
        .finally(() => {
          clearInterval(interval);
          this.isLoading = false;
        });
    },
    getDateTime(parsedData) {
      try {
        const datePart = parsedData.date.split("T")[0];
        const timePart = parsedData.time;
        return new Date(`${datePart}T${timePart}:00`);
      } catch (error) {
        return new Date();
      }
    },
    getAmount(parsedData) {
      var amount = parsedData.amount;
      try {
        if (Array.isArray(amount)) {
          return Math.max(...amount);
        } else {
          return amount;
        }
      } catch (error) {
        return 0;
      }
    },

    getMerchantName(parsedData) {
      try {
        return parsedData.merchant_name;
      } catch (error) {
        return "";
      }
    },
    extract() {
      this.extractImageFromUrl(this.billImageUrl);
    },

    uploaded(data) {
      var self = this;
      self.uploadedFiles = data.uploadedFiles;
      if (self.uploadedFiles.length > 0) {
        self.$set(self.obj, "documentId", self.uploadedFiles[0].id);
      }
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            const obj = response.result || {};
            obj.totalAmount = Number(obj.totalAmount || 0).toFixed(2); // input value
            self.obj = obj;
            self.billDateTime = obj.date;
            self.selectedChartOfAccount = obj.chartAccount;
          })
          .catch((err) => {
            console.error("Load receipt failed", err);
            self.$toast.error("Couldn't load this receipt. Check your connection and try again.");
          });
      } else {
        self.obj = self.getEmptyObj();
      }
    },
    setDateFilter(e) {
      this.billDateTime = new Date(e.target.value).getTime();
    },
    onSubmit() {
      var self = this;

      self.obj.date = toApiInstant(self.billDateTime);
      if (self.selectedChartOfAccount) {
        self.obj.chartAccount = self.selectedChartOfAccount;
        self.obj.chartAccountId = self.selectedChartOfAccount.id;
      } else {
        self.obj.chartAccount = null;
        self.obj.chartAccountId = null;
      }

      self.saving = true;
      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.saving = false;
            self.obj = response.result;
            self.$toast.success("Receipt added");
            self.$router.push({ path: `/tenants/Receipt/${self.obj.id}` });
          })
          .catch(({ data } = {}) => {
            self.saving = false;
            self.$toast.error("Couldn't save the receipt. " + helper.getErrorMessage(data, "Please try again."));
          });
      } else {
        this.api
          .update(self.obj)
          .then(() => {
            self.saving = false;
            self.$toast.success("Receipt saved");
          })
          .catch(({ data } = {}) => {
            self.saving = false;
            self.$toast.error("Couldn't save the receipt. " + helper.getErrorMessage(data, "Please try again."));
          });
      }
    },
    getEmptyObj() {
      return {
        date: new Date(),
        name: "",
        documentId: "",
        drawCompany: {},
      };
    },
    addNew() {
      this.$router.push({ path: "/tenants/Receipt" });
    },
    previous() {
      var self = this;
      this.api
        .getPrev(self.obj.id)
        .then((response) => {
          var nextObj = response.result;
          this.$router.push({ path: `/tenants/Receipt/${nextObj.id}` });
        })
        .catch(() => {
          self.$toast.info("Couldn't open the previous receipt. This may be the first one");
        });
    },
    next() {
      var self = this;
      this.api
        .getNext(self.obj.id)
        .then((response) => {
          var nextObj = response.result;
          this.$router.push({ path: `/tenants/Receipt/${nextObj.id}` });
        })
        .catch(() => {
          self.$toast.info("Couldn't open the next receipt. This may be the last one");
        });
    },
    submit() {
      this.submitted = true;
      this.onSubmit();
    },
    backToList() {
      this.$router.push({ path: "/tenants/receiptList" });
    },
  },
};
</script>